import React, {
  createContext,
  ReactNode,
  useState,
  useContext,
  Dispatch,
  SetStateAction,
} from "react";

// Types for Toast Message
interface ToastType {
  message: string;
  type: "success" | "error" | "info"; // Types of toast
}

// Context Type
interface ToastContextType {
  toast: ToastType | null;
  setToast: Dispatch<SetStateAction<ToastType | null>>;
  showToast: (message: string, type: "success" | "error" | "info") => void;
}

// Create the ToastContext
export const ToastContext = createContext<ToastContextType | undefined>(
  undefined
);

type ToastProviderProps = {
  children: ReactNode;
};

export default function ToastProvider({ children }: ToastProviderProps) {
  const [toast, setToast] = useState<ToastType | null>(null);

  // Show Toast Function
  const showToast = (message: string, type: "success" | "error" | "info") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000); // Auto-hide after 5 seconds
  };

  return (
    <ToastContext.Provider value={{ toast, setToast, showToast }}>
      {children}

      {/* Render Toast Component */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center p-4 rounded-lg shadow-lg max-w-xs ${
            toast.type === "success"
              ? "bg-green-100 text-primary-dark"
              : "bg-red-100 text-error"
          }`}
        >
          {/* Improved Icons */}
          {toast.type === "success" ? (
            <div className="w-8 h-8 flex items-center justify-center bg-primary-dark text-white rounded-full mr-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2.5}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                />
              </svg>
            </div>
          ) : (
            <div className="w-8 h-8 flex items-center justify-center bg-error text-white rounded-full mr-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2.5}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          )}

          {/* Message */}
          <span className="flex-1 text-sm font-medium">{toast.message}</span>
        </div>
      )}
    </ToastContext.Provider>
  );
}

// Custom Hook to use ToastContext
export function useToastContext() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToastContext must be used within a ToastProvider");
  }
  return context;
}
